import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { UserPlus, Users, Star, Trash2, Phone, Mail, BadgeCheck, FolderPlus, ShieldCheck, Link2, Pencil } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import CallButton from "@/components/CallButton";
import QuickContactActions from "@/components/QuickContactActions";

export default function ClientContactsTab({ clientId, projects }) {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [portal, setPortal] = useState(null);
  const [busy, setBusy] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", title: "", phone: "", is_primary: false, custom_role_id: "" });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get(`/clients/${clientId}/contacts`);
      setRows(data || []);
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [clientId]);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/custom-roles`).then((r) => setCustomRoles(r.data || [])).catch(() => {});
  }, [activeWorkspaceId]);
  useEffect(() => {
    // Try to fetch existing portal (no auto-create) — we use POST endpoint which is idempotent
    api.post(`/clients/${clientId}/portal`).then((r) => setPortal(r.data)).catch(() => setPortal(null));
  }, [clientId]);

  const roleNameById = (id) => customRoles.find((r) => r.id === id)?.name;

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/clients/${clientId}/contacts`, {
        ...form,
        custom_role_id: form.custom_role_id || null,
      });
      toast.success("Contact added");
      setOpen(false);
      setForm({ name: "", email: "", title: "", phone: "", is_primary: false, custom_role_id: "" });
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to add contact"));
    } finally { setSaving(false); }
  };

  const togglePrimary = async (c) => {
    try {
      await api.patch(`/client-contacts/${c.id}`, { is_primary: !c.is_primary });
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  const setRole = async (c, custom_role_id) => {
    try {
      await api.patch(`/client-contacts/${c.id}`, { custom_role_id: custom_role_id || null });
      toast.success("Role updated");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  const archive = async (c) => {
    try {
      await api.delete(`/client-contacts/${c.id}`);
      toast.success("Contact archived");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Could not archive")); }
  };

  const assign = async (c, projectId) => {
    if (!projectId) return;
    try {
      await api.post(`/projects/${projectId}/assign-contact`, { contact_id: c.id });
      const p = projects.find((x) => x.id === projectId);
      toast.success(`Assigned ${c.name} to ${p?.name || "project"}`);
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Assign failed")); }
  };

  const [editing, setEditing] = useState(null);
  const copyPortalLink = (c) => {
    if (!portal) {
      toast.error("Portal not ready. Click 'Client portal' to initialize.");
      return;
    }
    const url = `${window.location.origin}/portal/${portal.token}?c=${c.id}`;
    navigator.clipboard.writeText(url);
    toast.success(`Personal portal link copied for ${c.name}`);
  };

  return (
    <>
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm text-muted-foreground">{rows.length} contact{rows.length === 1 ? "" : "s"}</div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-none" data-testid="client-add-contact-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Add contact
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add contact</DialogTitle>
              <DialogDescription>Stakeholder at this client. They can be invited to specific projects later.</DialogDescription>
            </DialogHeader>
            <form onSubmit={create} className="space-y-3">
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Full name</Label>
                <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="contact-form-name" />
              </div>
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Email</Label>
                <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="contact-form-email" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="label-eyebrow mb-1 inline-block">Title</Label>
                  <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Marketing Manager" data-testid="contact-form-title" />
                </div>
                <div>
                  <Label className="label-eyebrow mb-1 inline-block">Phone</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="contact-form-phone" />
                </div>
              </div>
              <div>
                <Label className="label-eyebrow mb-1 inline-block">Portal role</Label>
                <Select value={form.custom_role_id || "_none"} onValueChange={(v) => setForm({ ...form, custom_role_id: v === "_none" ? "" : v })}>
                  <SelectTrigger className="h-9 text-xs" data-testid="contact-form-role">
                    <SelectValue placeholder="Default (external — view only)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Default (external — view only)</SelectItem>
                    {customRoles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-1">Defines what the contact can do on projects they're assigned to.</p>
              </div>
              <label className="flex items-center justify-between border border-border p-3 text-sm">
                <span className="inline-flex items-center gap-2"><Star className="w-3.5 h-3.5 text-amber-500" /> Mark as primary contact</span>
                <Switch checked={form.is_primary} onCheckedChange={(v) => setForm({ ...form, is_primary: v })} data-testid="contact-form-primary" />
              </label>
              <Button type="submit" disabled={saving} className="w-full rounded-none" data-testid="contact-form-submit">{saving ? "Saving…" : "Add contact"}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {busy && rows.length === 0 ? (
        <div className="text-sm text-muted-foreground">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="No contacts yet"
          description="Add stakeholders so you can assign them to specific projects under this client."
          icon={Users}
          testid="contacts-empty"
        />
      ) : (
        <div className="border border-border divide-y divide-border" data-testid="contacts-list">
          {rows.map((c) => (
            <div key={c.id} className="px-4 py-3 grid grid-cols-12 gap-3 items-start" data-testid={`contact-row-${c.id}`}>
              <div className="col-span-3 min-w-0">
                <div className="font-medium truncate flex items-center gap-2">
                  {c.name}
                  <QuickContactActions
                    entityType="contact" entityId={c.id} entityName={c.name}
                    phone={c.phone} email={c.email}
                  />
                  {c.is_primary && <span className="chip text-[10px] inline-flex items-center gap-1"><BadgeCheck className="w-3 h-3" /> primary</span>}
                </div>
                {c.title && <div className="text-xs text-muted-foreground truncate">{c.title}</div>}
                <div className="text-[11px] text-muted-foreground truncate inline-flex items-center gap-1 mt-1"><Mail className="w-3 h-3" /> {c.email}</div>
                {c.phone && (
                  <div className="text-[11px] text-muted-foreground truncate inline-flex items-center gap-1">
                    <Phone className="w-3 h-3" /> {c.phone}
                    <CallButton phone={c.phone} customer_name={c.name}
                      entity_type="contact" entity_id={c.id} entity_name={c.name}
                      className="rounded-none ml-1 h-6 px-1.5"
                      testid={`contact-call-${c.id}`} />
                  </div>
                )}
              </div>
              <div className="col-span-3 min-w-0">
                <Select value={c.custom_role_id || "_none"} onValueChange={(v) => setRole(c, v === "_none" ? null : v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid={`contact-role-${c.id}`}>
                    <SelectValue placeholder="External — view only" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">External — view only</SelectItem>
                    {customRoles.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-muted-foreground mt-1 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" /> Portal access role</p>
              </div>
              <div className="col-span-3">
                <Select onValueChange={(v) => assign(c, v)} value="">
                  <SelectTrigger className="h-8 text-xs" data-testid={`contact-assign-${c.id}`}>
                    <SelectValue placeholder="Assign to project…" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.length === 0 && <SelectItem value="_none" disabled>No projects under this client</SelectItem>}
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {(c.project_memberships || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`contact-projects-${c.id}`}>
                    {c.project_memberships.map((pm) => (
                      <span key={pm.project_id} className="chip text-[10px] inline-flex items-center gap-1" title={roleNameById(pm.custom_role_id) || "external"}>
                        <FolderPlus className="w-2.5 h-2.5" /> {pm.project_name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="col-span-3 flex items-center justify-end gap-1">
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setEditing(c)} title="Edit CRM details" data-testid={`contact-edit-${c.id}`}>
                  <Pencil className="w-3 h-3 mr-1" /> Edit
                </Button>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => copyPortalLink(c)} title="Copy personal portal link" data-testid={`contact-portal-link-${c.id}`}>
                  <Link2 className="w-3 h-3 mr-1" /> Portal link
                </Button>
                <Button variant="ghost" size="icon" className="h-7 w-7" title={c.is_primary ? "Unset primary" : "Set primary"} onClick={() => togglePrimary(c)} data-testid={`contact-star-${c.id}`}>
                  <Star className={`w-3.5 h-3.5 ${c.is_primary ? "fill-amber-500 text-amber-500" : ""}`} />
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`contact-archive-${c.id}`}>
                      <Trash2 className="w-3.5 h-3.5 text-destructive" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Archive this contact?</AlertDialogTitle>
                      <AlertDialogDescription>
                        {c.name} will no longer appear here. They remain assigned to any projects you already added them to.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => archive(c)} data-testid={`contact-archive-confirm-${c.id}`}>Archive</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <ContactCrmDialog
          contact={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => { await load(); setEditing(null); }}
        />
      )}
    </>
  );
}

const LIFECYCLE = [
  { v: "lead", label: "Lead" },
  { v: "prospect", label: "Prospect" },
  { v: "active", label: "Active" },
  { v: "inactive", label: "Inactive" },
  { v: "churned", label: "Churned" },
];
const COMM = [
  { v: "email", label: "Email" },
  { v: "phone", label: "Phone" },
  { v: "sms", label: "SMS" },
  { v: "whatsapp", label: "WhatsApp" },
];

function ContactCrmDialog({ contact, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: contact.name || "",
    title: contact.title || "",
    phone: contact.phone || "",
    lifecycle_stage: contact.lifecycle_stage || "",
    department: contact.department || "",
    linkedin_url: contact.linkedin_url || "",
    twitter_handle: contact.twitter_handle || "",
    communication_preference: contact.communication_preference || "",
    birthday: contact.birthday || "",
    notes: contact.notes || "",
  });
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      // Core fields via existing PATCH endpoint
      await api.patch(`/client-contacts/${contact.id}`, {
        name: form.name, title: form.title, phone: form.phone,
      });
      // CRM fields via dedicated endpoint
      await api.patch(`/client-contacts/${contact.id}/crm`, {
        lifecycle_stage: form.lifecycle_stage || null,
        department: form.department || null,
        linkedin_url: form.linkedin_url || null,
        twitter_handle: form.twitter_handle || null,
        communication_preference: form.communication_preference || null,
        birthday: form.birthday || null,
        notes: form.notes || null,
      });
      toast.success("Contact updated");
      await onSaved?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-auto" data-testid="contact-crm-dialog">
        <DialogHeader>
          <DialogTitle>Edit {contact.name}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Full name</Label>
              <Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="ccrm-name" />
            </div>
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Title</Label>
              <Input value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="ccrm-title" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Department</Label>
              <Input value={form.department} onChange={(e) => set("department", e.target.value)} placeholder="Marketing, Procurement…" data-testid="ccrm-department" />
            </div>
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Phone</Label>
              <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="ccrm-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Lifecycle stage</Label>
              <Select value={form.lifecycle_stage || "_none"} onValueChange={(v) => set("lifecycle_stage", v === "_none" ? "" : v)}>
                <SelectTrigger className="h-10" data-testid="ccrm-lifecycle"><SelectValue placeholder="Pick a stage" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">—</SelectItem>
                  {LIFECYCLE.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Preferred channel</Label>
              <Select value={form.communication_preference || "_none"} onValueChange={(v) => set("communication_preference", v === "_none" ? "" : v)}>
                <SelectTrigger className="h-10" data-testid="ccrm-channel"><SelectValue placeholder="No preference" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_none">—</SelectItem>
                  {COMM.map((s) => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">LinkedIn URL</Label>
              <Input value={form.linkedin_url} onChange={(e) => set("linkedin_url", e.target.value)} placeholder="https://linkedin.com/in/…" data-testid="ccrm-linkedin" />
            </div>
            <div>
              <Label className="block mb-1.5 text-sm font-semibold">Twitter / X</Label>
              <Input value={form.twitter_handle} onChange={(e) => set("twitter_handle", e.target.value)} placeholder="@handle" data-testid="ccrm-twitter" />
            </div>
          </div>
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Birthday</Label>
            <Input type="date" value={form.birthday} onChange={(e) => set("birthday", e.target.value)} className="h-10" data-testid="ccrm-birthday" />
          </div>
          <div>
            <Label className="block mb-1.5 text-sm font-semibold">Internal notes</Label>
            <textarea
              rows={3}
              value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              className="w-full border border-input bg-background px-3 py-2 text-sm"
              data-testid="ccrm-notes"
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-3">
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-none" data-testid="ccrm-save">{busy ? "Saving…" : "Save"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
